/**
 * 数据清理 - 选择性删除测试数据
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trash2, AlertTriangle, X, Users, CreditCard, Database,
  Share2, Activity, FileText, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const CLEANUP_OPTIONS = [
  {
    id: 'test_users',
    name: '删 4 个测试用户',
    icon: Users,
    color: 'text-accent-warning',
    desc: '删除 M/N/O/P(id 13-16)+ 他们的支付/分佣/推荐链/登录日志。其它数据保留。',
    danger: 'medium',
    label: '只删 4 个测试用户',
  },
  {
    id: 'all_test',
    name: '清空所有测试数据',
    icon: Trash2,
    color: 'text-accent-danger',
    desc: '删 M/N/O/P + 所有支付单 + 所有分佣 + 重置所有用户的 total_commission / direct_count。',
    danger: 'high',
    label: '清空所有测试数据(推荐)',
  },
  {
    id: 'test_payments',
    name: '清空所有支付',
    icon: CreditCard,
    color: 'text-accent-danger',
    desc: '删除 payments 表所有记录 + 清零所有 total_commission + 删 commissions。',
    danger: 'high',
    label: '只清支付 + 分佣',
  },
  {
    id: 'test_commissions',
    name: '只清分佣',
    icon: Share2,
    color: 'text-accent-warning',
    desc: '删除 commissions 表所有记录,清零 total_commission,保留支付单。',
    danger: 'medium',
    label: '只清分佣记录',
  },
  {
    id: 'reset_periods',
    name: '清空开奖数据',
    icon: Database,
    color: 'text-accent-warning',
    desc: '删除 ssq_periods + fetch_logs + user_records。',
    danger: 'medium',
    label: '清空开奖 + 抓取日志',
  },
  {
    id: 'reset_all',
    name: '⚠️ 重置整个数据库',
    icon: AlertTriangle,
    color: 'text-accent-danger',
    desc: '删除所有 18 张表的所有数据,不可恢复!只保留表结构。',
    danger: 'extreme',
    label: '重置整个数据库(危险)',
  },
];

export function CleanupView() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);

  async function loadStats() {
    setLoading(true);
    try {
      const res = await api.admin.diagnostics();
      setStats(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadStats(); }, []);

  async function doCleanup(mode) {
    setLoading(true);
    try {
      const res = await api.admin.cleanup(mode);
      toast.success(`清理完成:删 ${Object.entries(res.deleted).map(([k, v]) => `${k}=${v}`).join(', ')}`);
      setConfirming(null);
      loadStats();
    } catch (e) {
      toast.error('清理失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-2.5">
      {/* 当前数据状态 */}
      <div className="card !p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-brand-cyan" />
            <span className="text-xs font-semibold">当前数据状态</span>
          </div>
          <button onClick={loadStats} className="btn-ghost text-[10px] flex items-center gap-1 py-0.5 px-1.5">
            <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
            刷新
          </button>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
          <Stat label="用户" value={stats.users?.total || 0} />
          <Stat label="支付" value={stats.payments?.total || 0} />
          <Stat label="分佣" value={stats.commissions?.total || 0} />
          <Stat label="期数" value={stats.ssq_periods?.total || 0} />
          <Stat label="抓取日志" value={stats.fetch_logs?.total || 0} />
          <Stat label="错误日志" value={stats.error_logs?.total || 0} />
        </div>
      </div>

      {/* 警告 */}
      <div className="card !p-3 bg-accent-warning/5 border border-accent-warning/20">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-accent-warning flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-ink-2">
            <div className="font-semibold text-accent-warning mb-1">⚠️ 数据清理是危险操作</div>
            <ul className="space-y-0.5 text-ink-3 list-disc pl-4">
              <li>删除后 <b>不可恢复</b>(除非有备份)</li>
              <li>删除前请确认数据是测试数据</li>
              <li>真实用户/支付不要随便删</li>
              <li>推荐先做"数据导出"备份:看板右上角 → 导出数据</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 清理选项 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {CLEANUP_OPTIONS.map(opt => (
          <CleanupCard
            key={opt.id}
            opt={opt}
            onConfirm={() => setConfirming(opt)}
            loading={loading}
          />
        ))}
      </div>

      {/* 确认弹窗 */}
      <AnimatePresence>
        {confirming && (
          <ConfirmModal
            opt={confirming}
            loading={loading}
            onCancel={() => setConfirming(null)}
            onConfirm={() => doCleanup(confirming.id)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-bg-base rounded-lg p-2 text-center">
      <div className="text-base font-bold font-mono">{value}</div>
      <div className="text-[10px] text-ink-3 mt-0.5">{label}</div>
    </div>
  );
}

function CleanupCard({ opt, onConfirm, loading }) {
  const Icon = opt.icon;
  return (
    <motion.button
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={onConfirm}
      disabled={loading}
      className={clsx(
        'card !p-3 text-left transition border',
        opt.danger === 'extreme' ? 'border-accent-danger/30' :
        opt.danger === 'high' ? 'border-accent-warning/20' :
        'border-white/5'
      )}
    >
      <div className="flex items-start gap-2">
        <div className={clsx(
          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
          opt.danger === 'extreme' ? 'bg-accent-danger/10' :
          opt.danger === 'high' ? 'bg-accent-danger/5' :
          'bg-bg-base'
        )}>
          <Icon size={14} className={opt.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold mb-1">{opt.name}</div>
          <div className="text-[10px] text-ink-3 leading-relaxed">{opt.desc}</div>
        </div>
      </div>
    </motion.button>
  );
}

function ConfirmModal({ opt, loading, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('');
  const Icon = opt.icon;
  const danger = opt.danger === 'extreme' || opt.danger === 'high';
  const requiresTyping = opt.danger === 'extreme';
  const needsType = requiresTyping && typed !== 'DELETE';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ y: 30 }} animate={{ y: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md glass-strong rounded-2xl p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold flex items-center gap-2">
            <Icon size={16} className={opt.color} />
            {opt.name}
          </h3>
          <button onClick={onCancel}><X size={18} /></button>
        </div>
        <div className="text-xs text-ink-2 mb-3 leading-relaxed">{opt.desc}</div>
        {danger && (
          <div className={clsx(
            'rounded-lg p-2.5 mb-3 text-[11px]',
            opt.danger === 'extreme' ? 'bg-accent-danger/10 text-accent-danger' : 'bg-accent-warning/10 text-accent-warning'
          )}>
            ⚠️ 此操作<b>不可恢复</b>。请先做"数据导出"备份。
          </div>
        )}
        {requiresTyping && (
          <div className="mb-3">
            <label className="text-[10px] text-ink-3 mb-1 block">
              输入 <span className="font-mono font-bold text-accent-danger">DELETE</span> 确认
            </label>
            <input
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder="DELETE"
              className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-accent-danger/40"
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onCancel} className="btn-secondary text-sm">取消</button>
          <button
            onClick={onConfirm}
            disabled={loading || needsType}
            className={clsx(
              'text-sm py-2 rounded-lg font-semibold transition',
              'bg-accent-danger text-white disabled:opacity-50',
              'hover:bg-accent-danger/90'
            )}
          >
            {loading ? '清理中...' : '确认清理'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
